/**
 * web-sign-in-checkout D6, D7 — one booking on the website session, for the
 * booked and request-sent pages: its detail (the app's booking detail read),
 * whether its Booked or Pending message reached Telegram (re-read while still
 * on its way), the rows those pages draw, and Add to Calendar's choices.
 */
import { useQuery } from '@tanstack/react-query';
import { virtualPlatformLabel } from '@telify/shared/browser';
import { formatCurrency } from '@/components/payment/formatCurrency';
import { phoneCallStep } from '@/pages/booking/shared/bookingRules';
import type { BookingDetail } from '@/types/bookingDetail';
import { buildMapsUrl } from '@/utils/mapLinks';

import { CLIENT_SCOPE, useWebSession, webFetch } from '../api/session';
import { formatSlotTime } from '../format';

export type ConfirmationMessage = 'PENDING' | 'SENT' | 'REFUSED' | 'FAILED' | null;

/** How long the page keeps asking about a message still on its way. */
const MESSAGE_WAIT_MS = 30_000;

export function useWebBooking(bookingId: string) {
  const signedIn = useWebSession().status === 'signedIn';
  const detail = useQuery({
    queryKey: [...CLIENT_SCOPE, 'booking', bookingId],
    queryFn: async () => (await webFetch<{ booking: BookingDetail }>(`/booking-hub/booking/${encodeURIComponent(bookingId)}`)).booking,
    enabled: signedIn && Boolean(bookingId),
    retry: false,
  });
  const message = useQuery({
    queryKey: [...CLIENT_SCOPE, 'booking-message', bookingId],
    queryFn: async () =>
      (await webFetch<{ confirmationMessage: ConfirmationMessage }>(`/booking/${encodeURIComponent(bookingId)}`)).confirmationMessage,
    enabled: signedIn && Boolean(bookingId),
    retry: false,
    refetchInterval: (q) => (q.state.data === 'PENDING' && Date.now() - q.state.dataUpdatedAt < MESSAGE_WAIT_MS ? 3000 : false),
  });
  return { detail, message: message.data ?? null };
}

export interface BookedRow {
  /** When, Requested, Length, Format, Where, Paid, Due, Price, Payment. */
  readonly label: string;
  readonly value: string;
  /** The smaller second line, or null. */
  readonly sub: string | null;
  /** The amount on the right, or null. */
  readonly amount: string | null;
  /** On Where for a place: the directions link; null otherwise. */
  readonly directionsHref: string | null;
}

function dateLong(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: tz }).format(new Date(iso));
}

/** The rows of the booked (or request-sent) page, in the prototype's order. */
export function bookedRows(b: BookingDetail, tz: string, request: boolean): BookedRow[] {
  const loc = b.location;
  const format = loc?.isByPhone ? 'By phone' : loc?.isVirtual ? 'Online' : 'In person';
  const rows: BookedRow[] = [
    { label: request ? 'Requested' : 'When', value: dateLong(b.scheduledAt, tz), sub: formatSlotTime(b.scheduledAt, tz), amount: null, directionsHref: null },
    b.service.type === 'individual'
      ? { label: 'Length', value: `${b.service.duration} min`, sub: format, amount: null, directionsHref: null }
      : { label: 'Format', value: 'Group event', sub: format, amount: null, directionsHref: null },
  ];
  if (loc?.isByPhone) {
    // service-by-phone D14: By phone with the call line, no directions.
    rows.push({ label: 'Where', value: 'By phone', sub: phoneCallStep(b.phoneCall)?.line ?? null, amount: null, directionsHref: null });
  } else if (loc?.isVirtual) {
    const platform = virtualPlatformLabel(loc.virtualPlatform);
    rows.push({ label: 'Where', value: 'Online', sub: platform ? `on ${platform}` : null, amount: null, directionsHref: null });
  } else if (loc && (loc.name || loc.city)) {
    rows.push({
      label: 'Where',
      value: loc.name ?? 'In person',
      sub: [loc.address, loc.city, loc.state].filter(Boolean).join(', ') || null,
      amount: null,
      directionsHref: buildMapsUrl(loc),
    });
  }
  rows.push(paymentRow(b, request));
  return rows;
}

/** The desktop ticket's payment row (the phone end screen's is bookingRules' endPaymentRow). */
function paymentRow(b: BookingDetail, request: boolean): BookedRow {
  const amount = b.payment?.amount ?? null;
  const none = { amount: null, directionsHref: null };
  if (!b.payment || !amount) return { label: 'Price', value: 'Nothing to pay', sub: null, ...none, amount: 'Free' };
  if (b.payment.status === 'PAID') return { label: 'Paid', value: 'by card', sub: null, ...none, amount: formatCurrency(amount) };
  if (request && b.paymentRequirement !== 'PAY_AT_SESSION') return { label: 'Payment', value: 'Collected after approval', sub: null, ...none };
  return { label: 'Due', value: 'at the session', sub: null, ...none, amount: formatCurrency(amount) };
}

/**
 * Add to Calendar's Apple Calendar (shared-client-screens D6): the booking's calendar file through a
 * five-minute, one-use ticket the session mints, opened in place (a Mac or iPhone opens it into its
 * calendar; elsewhere it downloads). Rejects when the ticket cannot be had.
 */
export async function openCalendarFile(b: Pick<BookingDetail, 'id'>): Promise<void> {
  const { url } = await webFetch<{ url: string }>(`/booking-hub/booking/${encodeURIComponent(b.id)}/calendar-ticket`, { method: 'POST' });
  window.location.assign(url);
}

/** The Telegram line (design D6): sent or on its way, or refused with Open chat; null otherwise. */
export function telegramLine(
  message: ConfirmationMessage,
  studio: { name: string; bookingsBotUsername: string | null } | undefined,
  request: boolean,
): { kind: 'sent' | 'refused'; text: string; chatHref: string | null } | null {
  if (!studio) return null;
  if (message === 'REFUSED') {
    return {
      kind: 'refused',
      text: `Open your chat with ${studio.name} in Telegram to get your confirmation and reminders.`,
      chatHref: studio.bookingsBotUsername ? `https://t.me/${studio.bookingsBotUsername}` : null,
    };
  }
  // A request's own message is the Request sent note; the page says nothing more about it.
  if (request || (message !== 'SENT' && message !== 'PENDING')) return null;
  return { kind: 'sent', text: `A booking confirmation was sent to your Telegram chat with ${studio.name}.`, chatHref: null };
}
